import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GroupHeader, GroupHome, GroupHomeProps } from './GroupHome';
import { GroupSetup } from './GroupSetup';
import { useGroupSetup } from './useGroupSetup';

interface Props extends GroupHomeProps {
  /** Loads the group's balances again; called when the user comes back to a group still being set up. */
  onRefresh?: () => void;
  /** The group's balances are on their way. */
  balancesLoading?: boolean;
}

/**
 * A group's Início: "Monte o grupo" until the group's first entry, ever,
 * then the dashboard. A new person never lands on a page of zeros.
 */
export const GroupView: React.FC<Props> = ({ onRefresh, balancesLoading = false, ...props }) => {
  const { group, balances, currentUserId, onInvite } = props;
  const { t } = useTranslation('common');
  const { entries, signupInvitations } = useGroupSetup(group, { balances, onRefresh });
  // "Monte o grupo" waits for what its steps are read from (the people and
  // the open invitations), so no step, count or filled button jumps once it shows.
  const setupWaiting =
    entries === 0 && ((balancesLoading && balances?.contextId !== group.id) || signupInvitations === null);

  if (entries === null || setupWaiting) {
    // The name is known already; only what goes under it is waiting.
    return (
      <Box sx={{ display: 'grid', gap: { xs: 2, md: 2.5 } }}>
        <GroupHeader group={group} balances={balances} currentUserId={currentUserId} />
        <Box role='status' aria-busy='true' aria-label={t('messages.loading')}>
          <Skeleton variant='rounded' height={320} sx={{ borderRadius: '22px' }} />
        </Box>
      </Box>
    );
  }

  if (entries === 0) {
    return (
      <GroupSetup
        group={group}
        balances={balances}
        currentUserId={currentUserId}
        signupInvitations={signupInvitations ?? 0}
        onInvite={onInvite}
      />
    );
  }

  return <GroupHome {...props} />;
};
